import { useSyncExternalStore } from 'react';

const MINUTE_MS = 60_000;

type MinuteClockStore = {
  subscribe: (onStoreChange: () => void) => () => void;
  getSnapshot: () => number;
  dispose: () => void;
};

function quantizeToMinute(timeMs: number) {
  return Math.floor(timeMs / MINUTE_MS) * MINUTE_MS;
}

function createMinuteClockStore(): MinuteClockStore {
  let snapshot = quantizeToMinute(Date.now());
  const listeners = new Set<() => void>();
  let timerId: ReturnType<typeof setTimeout> | undefined;

  const emit = () => {
    const next = quantizeToMinute(Date.now());
    if (next === snapshot) {
      return;
    }
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const arm = () => {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    const delay = MINUTE_MS - (Date.now() % MINUTE_MS);
    timerId = setTimeout(() => {
      timerId = undefined;
      emit();
      if (listeners.size > 0) {
        arm();
      }
    }, delay);
  };

  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    emit();
    if (listeners.size > 0) {
      arm();
    }
  };

  const stop = () => {
    if (timerId !== undefined) {
      clearTimeout(timerId);
      timerId = undefined;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
    }
  };

  return {
    subscribe(onStoreChange) {
      listeners.add(onStoreChange);
      if (listeners.size === 1) {
        if (typeof document !== 'undefined') {
          document.addEventListener('visibilitychange', onVisible);
        }
        arm();
      }
      return () => {
        listeners.delete(onStoreChange);
        if (listeners.size === 0) {
          stop();
        }
      };
    },
    getSnapshot() {
      if (listeners.size === 0) {
        snapshot = quantizeToMinute(Date.now());
      }
      return snapshot;
    },
    dispose() {
      stop();
      listeners.clear();
    },
  };
}

let store: MinuteClockStore | undefined;

function getMinuteClockStore() {
  store ??= createMinuteClockStore();
  return store;
}

export function resetMinuteClockStore() {
  store?.dispose();
  store = undefined;
}

const subscribeNothing = () => () => {};
const readNothing = () => 0;

export function useMinuteClock(now?: number): number {
  const clock = getMinuteClockStore();
  const live = now === undefined;
  const value = useSyncExternalStore(
    live ? clock.subscribe : subscribeNothing,
    live ? clock.getSnapshot : readNothing,
    live ? clock.getSnapshot : readNothing,
  );
  return now ?? value;
}
